import axios from 'axios';
import { attachAuthInterceptor } from './authInterceptor';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Movement Analysis API — stock-item movement (purchases inbound vs sales
 * outbound) by weight, product-wise, with drill-down.
 * ADMIN / SUPER_ADMIN only. date_field = challan_date.
 * Base: `${API_BASE_URL}/api/v1/movement-analytics`
 */
const movementAnalyticsAPI = axios.create({
    baseURL: `${API_BASE_URL}/api/v1/movement-analytics`,
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: warehouseParamsSerializer,
});

// Session-expiry handling (401 -> clear + redirect), consistent with feature services.
attachAuthInterceptor(movementAnalyticsAPI);

/**
 * When a request uses responseType:'blob', an error response body is also a
 * Blob (not parsed JSON). Read it back to JSON and extract `error.message`.
 */
async function blobErrorMessage(error) {
    try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
            const text = await data.text();
            const json = JSON.parse(text);
            return json?.error?.message || json?.message || null;
        }
    } catch (e) {
        // fall through to caller's generic extractor
    }
    return null;
}

movementAnalyticsAPI.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return applyWarehouseFilter(config);
    },
    (error) => Promise.reject(error)
);

const movementAnalyticsService = {
    /** Headline totals: inbound vs outbound, net flow, top mover, biggest gap. */
    async getSummary(params = {}, options = {}) {
        try {
            const response = await movementAnalyticsAPI.get('/summary', { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) return { success: false, error: 'Request canceled', isCanceled: true };
            return { success: false, error: extractApiError(error) };
        }
    },

    /** Per-product purchase vs sales roll-up (the clickable list). */
    async getProducts(params = {}, options = {}) {
        try {
            const response = await movementAnalyticsAPI.get('/products', { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) return { success: false, error: 'Request canceled', isCanceled: true };
            return { success: false, error: extractApiError(error) };
        }
    },

    /**
     * Flat drill-down for a Sub Category: its purchase advices + sales sub-orders.
     * `catalogueId` is the catalogue (Sub Category) id from the /products list.
     */
    async getProductDetail(catalogueId, params = {}, options = {}) {
        try {
            const response = await movementAnalyticsAPI.get(`/product/${catalogueId}`, { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) return { success: false, error: 'Request canceled', isCanceled: true };
            return { success: false, error: extractApiError(error) };
        }
    },

    /**
     * Drill level 1 — Sub Category → Product (size) split.
     * `catalogueId` is the catalogue (Sub Category) id from the /products list.
     * Each returned product_id is a ProductSize id (feed it to getProductOrders, NOT getProductDetail).
     */
    async getSubCategoryProducts(catalogueId, params = {}, options = {}) {
        try {
            const response = await movementAnalyticsAPI.get(`/sub-category/${catalogueId}/products`, { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) return { success: false, error: 'Request canceled', isCanceled: true };
            return { success: false, error: extractApiError(error) };
        }
    },

    /**
     * Drill level 2 — Product (size) → parent Orders / POs.
     * `sizeId` is a ProductSize id from the sub-category split.
     */
    async getProductOrders(sizeId, params = {}, options = {}) {
        try {
            const response = await movementAnalyticsAPI.get(`/product/${sizeId}/orders`, { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) return { success: false, error: 'Request canceled', isCanceled: true };
            return { success: false, error: extractApiError(error) };
        }
    },

    /**
     * Export the Sub Category → Product (size) split as a CSV file.
     * Returns the raw blob + headers so the caller can trigger a download.
     * `catalogueId` is the catalogue (Sub Category) id; pass the same filters
     * the split modal is showing so the export matches the screen.
     */
    async exportSubCategoryProducts(catalogueId, params = {}) {
        try {
            const response = await movementAnalyticsAPI.get(
                `/sub-category/${catalogueId}/products/export`,
                { params, responseType: 'blob' }
            );
            return { success: true, data: response.data, headers: response.headers };
        } catch (error) {
            // On error the body is JSON even though we requested a blob — parse it back.
            const msg = await blobErrorMessage(error);
            return { success: false, error: msg || extractApiError(error) };
        }
    },
};

export default movementAnalyticsService;
